import { useEffect, useMemo, useState } from 'react';
import { getIn, useFormikContext } from 'formik';
import hash from 'object-hash';

import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { FileCategoryId } from '@e-socna/data-layer';

import { CheckboxList } from '../../../components/CheckboxList';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFormikOnNextTick } from '../../../hooks/useFormikOnNextTick.hook';
import {
  buildMissionFileCategoryTree,
  toMissionFileCategoryCheckboxListItems,
} from '../utils/mission-file-category-tree.util';

import type { ResolvableMissionTypeForm_ResolvableMissionType } from '../fragments/resolvable-mission-type.fragment';

interface Props {
  readonly id: string;
}

export const ResolvableMissionTypeFilesFormStep = ({}: Readonly<Props>) => {
  const { errors, setFieldTouched, touched, values } =
    useFormikContext<ResolvableMissionTypeForm_ResolvableMissionType>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const { missionFileCategories } = useReferencesContext();

  const treeMissionFileCategories = useMemo(
    () =>
      missionFileCategories.filter(
        (fileCategory) => !fileCategory.hideInFileExplorer,
      ),
    [missionFileCategories],
  );

  const allowedFileCategoryIds = getIn(values, 'allowedFileCategoryIds') as
    readonly FileCategoryId[] | null | undefined;

  const allowedFileCategoryCheckboxIdPrefix = `${hash(`${values._id}-allowedFileCategoryIds`)}-`;

  const [checkedAllowedFileCategoryIds, setCheckedAllowedFileCategoryIds] =
    useState(allowedFileCategoryIds);

  useEffect(() => {
    setFieldValueOnNextTick(
      'allowedFileCategoryIds',
      checkedAllowedFileCategoryIds,
    );
  }, [checkedAllowedFileCategoryIds, setFieldValueOnNextTick]);

  const missionFileCategoryTree = useMemo(
    () => buildMissionFileCategoryTree(treeMissionFileCategories),
    [treeMissionFileCategories],
  );

  return (
    <Grid container spacing={2}>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <FormControl
          component="fieldset"
          error={
            !!(errors.allowedFileCategoryIds && touched.allowedFileCategoryIds)
          }
          required
          sx={{ ml: 3, display: 'flex' }}
          variant="standard"
        >
          <FormLabel component="legend">
            Catégories de Fichiers Autorisées
          </FormLabel>
          {/* A category only becomes checkable once its parent is: allowing
              files under a sub-category is meaningless otherwise. */}
          <CheckboxList<FileCategoryId>
            childrenRequireParent
            idPrefix={allowedFileCategoryCheckboxIdPrefix}
            items={toMissionFileCategoryCheckboxListItems(
              missionFileCategoryTree,
            )}
            // eslint-disable-next-line react/jsx-no-bind
            onChange={(fileCategoryIds) => {
              setCheckedAllowedFileCategoryIds(fileCategoryIds);
              void setFieldTouched('allowedFileCategoryIds', true, false);
            }}
            value={checkedAllowedFileCategoryIds ?? EMPTY_ARRAY}
            variant="edition"
          />
          {!!(
            getIn(errors, 'allowedFileCategoryIds') &&
            getIn(touched, 'allowedFileCategoryIds')
          ) && (
            <FormHelperText>
              {getIn(errors, 'allowedFileCategoryIds')}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
    </Grid>
  );
};
